import React, { useState } from "react";
import DocumentLightbox, { isImageUrl } from "../../components/DocumentLightbox";

function formatIncomeRange(value: any): string {
  if (value == null) return "";
  const minVal = value.min ?? value.minAmount ?? value.minimum;
  const maxVal = value.max ?? value.maxAmount ?? value.maximum;
  if (typeof value === "object" && minVal != null && maxVal != null) {
    const min = Number(minVal);
    const max = Number(maxVal);
    if (!Number.isNaN(min) && !Number.isNaN(max)) {
      const fmt = (n: number) =>
        n.toLocaleString("en-US", { maximumFractionDigits: 0 });
      return `₦${fmt(min)} - ₦${fmt(max)}`;
    }
  }
  if (typeof value === "string") {
    const normalized = value.replace(/,/g, "").trim();
    const parts = normalized
      .split(/[\s_\-]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => Number(s))
      .filter((n) => !Number.isNaN(n));
    if (parts.length >= 2) {
      const fmt = (n: number) =>
        n.toLocaleString("en-US", { maximumFractionDigits: 0 });
      return `₦${fmt(parts[0])} - ₦${fmt(parts[1])}`;
    }
    const single = Number(normalized);
    if (!Number.isNaN(single))
      return `₦${single.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  }
  const num = Number(value);
  if (!Number.isNaN(num))
    return `₦${num.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  return String(value);
}

const Kyc3 = ({ customerDeet }: any) => {
  const [showImage, setShowImage] = useState<boolean>(false);
  const [imageUrl, setImageUrl] = useState<string>("");

  const kyc3 = customerDeet?.kyc3;
  const kyc3Data = kyc3?.data ?? {};

  const sourceOfIncome = kyc3Data?.sourceOfIncome;
  const monthlyIncomeRange = kyc3Data?.monthlyIncomeRange;
  const monthlyIncomeRangeFormatted =
    monthlyIncomeRange != null && String(monthlyIncomeRange).trim() !== ""
      ? formatIncomeRange(monthlyIncomeRange)
      : "";
  const employmentDocumentUrl = kyc3Data?.employmentDocumentUrl;

  const statusLabel =
    kyc3?.status === "success" ||
    kyc3?.status === "VERIFIED" ||
    kyc3?.status === "approved" ||
    kyc3?.status === "Approved"
      ? "Approved"
      : kyc3?.status === "rejected" || kyc3?.status === "Rejected"
      ? "Rejected"
      : kyc3?.status ?? "Pending";

  const statusColor =
    statusLabel === "Approved"
      ? "bg-[#0E2B1F] text-[#03C668] border-[#03C668]/30"
      : statusLabel === "Rejected"
      ? "bg-red-500/10 text-red-400 border-red-400/30"
      : "bg-[#CB9200]/10 text-[#F28A0F] border-[#F28A0F]/30";

  const openImage = (url: string) => {
    setImageUrl(url);
    setShowImage(true);
  };

  const has = (v: any) => v != null && String(v).trim() !== "";

  return (
    <div className="w-full bg-[#0D0D0D] p-4 sm:p-6 md:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h4 className="text-white font-sora font-bold text-[16px] sm:text-[18px]">
          Level 3 Verification
        </h4>
        <span
          className={`inline-flex items-center px-3 py-1.5 rounded-full text-[12px] font-semibold border ${statusColor} w-fit`}
        >
          {statusLabel?.toUpperCase()}
        </span>
          </div>

      <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Level 3 details card */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-xl border border-[#1C2227] bg-[#141414] p-5 sm:p-6">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-[#1C2227]">
              <div className="w-10 h-10 rounded-lg bg-[#0C84DA]/10 flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-[#0C84DA]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
          </div>
              <div>
                <h4 className="text-white font-sora font-bold text-[14px]">
                  Level 3 details
            </h4>
                <p className="text-[#869AA9] text-[12px]">
                  Income & employment
                </p>
          </div>
        </div>
            <div className="space-y-3">
              {has(sourceOfIncome) && (
                <div className="flex justify-between items-center py-2.5 border-b border-[#1C2227]/60">
                  <span className="text-[#869AA9] text-[12px] font-medium">
                    Source of income
                  </span>
                  <span className="text-white text-[12px] font-medium capitalize">
                    {sourceOfIncome}
                  </span>
                </div>
              )}
              {has(monthlyIncomeRangeFormatted) && (
                <div className="flex justify-between items-center py-2.5 border-b border-[#1C2227]/60">
                  <span className="text-[#869AA9] text-[12px] font-medium">
                    Monthly income range
                  </span>
                  <span className="text-[#0C84DA] text-[12px] font-semibold">
                    {monthlyIncomeRangeFormatted}
                  </span>
          </div>
              )}
            </div>
          </div>
        </div>

        {/* Employment document card - right column */}
        <div className="lg:col-span-1">
          <div className="rounded-xl border border-[#1C2227] bg-[#141414] p-5 sm:p-6 sticky top-4">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-[#1C2227]">
              <div className="w-10 h-10 rounded-lg bg-[#0C84DA]/10 flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-[#0C84DA]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <h4 className="text-white font-sora font-bold text-[14px]">
                  Employment document
            </h4>
                <p className="text-[#869AA9] text-[11px]">
                  Proof of employment / income
                </p>
              </div>
            </div>

            {employmentDocumentUrl ? (
              <>
                {isImageUrl(employmentDocumentUrl) ? (
                  <div className="rounded-lg border border-[#1C2227] overflow-hidden bg-[#0D0D0D] flex justify-center items-center min-h-[200px]">
                    <img
                      src={employmentDocumentUrl}
                      alt="Employment document"
                      className="max-h-[280px] w-full object-contain cursor-zoom-in hover:opacity-90 transition-opacity"
                      onClick={() => openImage(employmentDocumentUrl)}
                    />
                  </div>
                ) : (
                  <div className="rounded-lg border border-[#1C2227] bg-[#0D0D0D] p-6 flex flex-col items-center justify-center min-h-[160px] text-center">
                    <svg
                      className="w-12 h-12 text-[#617889] mb-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <p className="text-[#869AA9] text-[12px] mb-4">
                      Document available
                    </p>
                    <button
                      type="button"
                      onClick={() => openImage(employmentDocumentUrl)}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#0C84DA] text-white text-[13px] font-medium hover:bg-[#0a6bb8] transition-colors"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                      View document
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => openImage(employmentDocumentUrl)}
                  className="mt-4 inline-flex items-center gap-2 text-[#0C84DA] text-[13px] font-medium hover:underline"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-4.35-4.35M11 8v6M8 11h6M19 11a8 8 0 11-16 0 8 8 0 0116 0z"
                    />
                  </svg>
                  Enlarge
                </button>
              </>
            ) : (
              <div className="rounded-lg border border-[#1C2227] bg-[#0D0D0D] p-6 flex flex-col items-center justify-center min-h-[160px] text-center">
                <svg
                  className="w-12 h-12 text-[#617889] mb-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="text-[#617889] text-[12px]">
                  No document uploaded
                </p>
            </div>
            )}
          </div>
        </div>
      </div>

      {showImage && (
        <DocumentLightbox
          urls={[imageUrl]}
          title="Employment document"
          onClose={() => setShowImage(false)}
        />
      )}
    </div>
  );
};

export default Kyc3;
